/**
 * ExportToThrivePanel — the write-to-Thrive surface (subtask 07).
 *
 * The heading and CTA copy come from the PAGE via `actionLabel`, because the
 * verb is per-list-type since cl.4 item 9; the hard-coded "Export Selected to
 * Thrive" this file used to own put the retired verb one click behind the
 * rename. The default preserves the old string so the component stands alone.
 *
 * **It stopped being a second selection screen on 2026-08-27** (Trello #729
 * cl.8). It used to re-present the contacts the recruiter had ALREADY picked in
 * the table behind it, each with its own checkbox and a Select-all above them —
 * a confirmation step that asked the same question twice. Jared: replace it with
 * one line saying what is about to happen. So the panel now opens with the
 * selection settled and states it:
 *
 *     You are about to add 28 people to search ID 4414 NYCOO VP Finance
 *
 * The number in that line is the number that will be WRITTEN: the seeded
 * selection minus anything `isExportable` rejects (already in Thrive, W1
 * duplicate, mid-flight), and minus any W2/W3 conflict nobody has decided —
 * because `resolve_export_plan` skips those by default. `willBeWritten` is the
 * predicate, and it is decision-aware: a row the recruiter chose to "Update
 * anyway" in Compare is counted, because it will be written.
 *
 * THIS FILE USED TO DOCUMENT THE OPPOSITE, and that gap was the reported bug:
 * "a run of 28 can add 23", with the screen saying 28 and the conflicts line
 * printing the difference underneath. Stating both halves was better than
 * implying one, but a recruiter reads the number, not the footnote. The line
 * now says 23. The conflicts line survives with a changed job: it no longer
 * explains a gap between the sentence and reality, it explains why the number
 * is smaller than the selection.
 *
 * `isExportable` is unchanged, deliberately. Selectability and countability are
 * two questions, and one predicate answering both is the root cause rather than
 * an implementation detail — W2/W3 rows must STAY tickable so "Update anyway"
 * is reachable from here.
 *
 * Opened with NO selection — the Search-card CTA is not gated on one — it means
 * "add everyone who can be added". That arrives as a `selectAllScope` decided by
 * `ImportDetailPage`, not as a seed built here; see the settle effect below for
 * why building it here truncated every run to one page.
 *
 * **It stopped being a roster on 2026-09-09** (Trello #758). Under the sentence
 * it used to list every contact — avatar, name, a W1 note, a W2 `<select>`, a
 * per-row status — which re-presented the table behind it and still did not
 * answer the question the screen exists to ask. In its place: the STATS of the
 * last Compare to Thrive, in Compare's own words, through the shared
 * `CompareBucketList`.
 *
 * The numbers are read, never computed here — `GET /imports/{id}/compare`
 * returns the persisted `dedup_status` of the last run, so they survive a
 * reload and cost no second dry run. **This panel never STARTS a compare**; it
 * only reads one, and refuses to write without one (see `blocked` below). That
 * refusal is the one place the v37 rule bends: a CONFLICT still never disables
 * the write, but an import with NO SUCCESSFUL COMPARE ON RECORD now does.
 *
 * Per-IMPORT, not per-contact, and the difference is not pedantry: with one
 * successful compare stored, a select-all over 300 rows of which 298 were never
 * classified still writes, carrying only the coverage line. The gate asks "has
 * anyone ever compared this import?", never "are THESE contacts compared?"
 * (/pr-review iter 10, Agent C).
 *
 * Conflicts are stated as counts and resolved in the Compare modal. The
 * four-action control lives there; this panel used to ask the same question
 * with a two-option `<select>`, and two surfaces asking one question in two
 * vocabularies is the confusion the card is about.
 *
 * Live progress and, once a run settles, ONE aggregated result. Per-contact
 * outcomes are in `ContactsTable`, which already carries an outcome column and
 * a Thrive ID column. Drives the shared backend via `useExportToThrive`.
 *
 * ONE mount, despite what this docblock used to say. The "dedicated Export
 * split-view" of the EPIC's "same backend, two frontends" was never built, and
 * the claim survived here long enough to send a reviewer looking for it
 * (/pr-review iter 1, Agent C M5). Every prop defaulted "so the standalone
 * split-view is unchanged" is really defaulted for a propless render in a test.
 *
 * Design-system tokens only — no inline `style={{}}`, no arbitrary `text-[Npx]`.
 * English-only strings (workspace policy). Built against MSW from subtask 01.
 */

import { Button, ProgressBar, Spinner } from "@nu-assist/ui";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef } from "react";

import type {
  AddPreviewSkipReason,
  ContactSelectionFilters,
  ExportIntent,
  ExportStatus,
} from "@nu-assist/contracts";

import { ImportsApiError, type ImportsClient } from "../../clients/imports.client";
// The cap lives with the dialog that first enforced it, and is imported
// rather than duplicated: two copies of a limit that must equal the
// server's `EXPORT_CHUNK_SIZE` is one copy too many, and
// `maxExplicitAddSelection.pin.test.ts` pins exactly this symbol to the
// emitted contract.
import { MAX_EXPLICIT_ADD_SELECTION } from "./PreAddConfirmation";
import {
  EXPORT_COMPARE_FAILED_BODY,
  EXPORT_COMPARE_RUNNING_BODY,
  EXPORT_COMPARE_UNREADABLE_BODY,
  EXPORT_COMPARE_UNREADABLE_TITLE,
  EXPORT_NOT_COMPARED_BODY,
  EXPORT_NOT_COMPARED_TITLE,
  EXPORT_PREVIEW_UNAVAILABLE,
  EXPORT_RESULT_DETAIL_POINTER,
  EXPORT_RESULT_UNKNOWN,
  EXPORT_RETRY_READ_CTA,
  EXPORT_RUN_COMPARE_CTA,
  exportNeedsCompareNote,
  exportNotAddedNote,
  exportNothingWrittenLine,
  exportResultHeading,
  exportResultNeedsCompareLine,
  exportResultPendingLine,
  exportTooManySelected,
} from "./exportConfirmationCopy";
import { isExportable } from "./isExportable";
import { type W2Decision, useExportToThrive } from "./useExportToThrive";

/**
 * A 404 from `GET /compare` is the ONE rejection that means "no compare run has
 * ever been started" (`service_compare.py` raises it for exactly that, and for a
 * missing import). Every other failure — 5xx, a timeout, an edge 403 with an
 * HTML body — is us being unable to read, and saying "not compared yet" there
 * blames the recruiter for our outage.
 *
 * Defensive on the type because `queryFn` rejections are `unknown`: a network
 * failure rejects with a plain `TypeError`, which is not an `ImportsApiError`
 * and is correctly NOT a 404.
 */
function isNotFound(error: unknown): boolean {
  return error instanceof ImportsApiError && error.status === 404;
}

/**
 * Failed reads of `GET /compare` the panel absorbs before it stops re-reading
 * and leaves the retry to the `Try again` button.
 *
 * CONSECUTIVE failures — counted against a baseline taken at the last
 * successful read, because no built-in counter means that (see the bound
 * itself). Produces 5 requests in all from a cold start (the first, plus 4
 * automatic re-reads), and starts over after any read that lands.
 *
 * `retry: false` plus an interval on the error state means the interval IS the
 * retry, and an unbounded one turns every open panel into a poller against an
 * endpoint that is already failing. Five covers the blip this exists for; a
 * sustained outage is not something to keep asking about.
 */
const READ_RETRY_LIMIT = 5;

/** The `not_added` causes the panel states on their own line, above the
 *  Compare to Thrive button, rather than in the "Not added" note (sub-task 04). */
const COMPARE_LINE_CAUSES: readonly AddPreviewSkipReason[] = [
  "not_compared_no_thrive_check",
  "compare_too_old",
];

export interface ExportToThrivePanelProps {
  client: ImportsClient;
  importId: string;
  onTerminal?: (status: ExportStatus) => void;
  /** Forwarded to the hook. `origin` distinguishes a run that FINISHED from a
   *  POST that failed to start, and `kind` carries `"info"` for a run whose
   *  outcome the payload cannot answer — see `useExportToThrive`. */
  notify?: (
    message: string,
    kind: "success" | "error" | "info",
    origin?: "terminal" | "start"
  ) => void;
  /**
   * What this panel's write action is CALLED, supplied by the page so the
   * whole flow uses one verb. Both CTAs that open this panel say "Add
   * Selected to search" (or "…to Thrive" on a Company Map); a panel headed
   * "Export Selected to Thrive" put the retired verb one click behind the
   * rename, which is the same one-action-two-names problem §13 of the
   * 2026-08-18 call was about. Caught by `/pr-review` iter 1 (Agent A 7(a)).
   * The default is the pre-rename string, kept so the component renders
   * sensibly with no props in a test. It is NOT a second surface: there is
   * exactly one mount of this component in the app, and the "standalone Export
   * split-view" the old comment sent readers looking for was never built
   * (/pr-review iter 1, Agent C M5).
   */
  actionLabel?: string;
  /**
   * Button copy while a run is in flight. Same reason `actionLabel` exists: the
   * heading names the action and the button used to answer with a generic
   * "Working…", the one word on the surface belonging to neither the retired
   * "Export" verb nor the new "Add" one (/pr-review iter 1, Agent C M3).
   * Same defaulting rule as `actionLabel`, and the same correction: the
   * default exists for a propless render, not for another surface.
   */
  runningLabel?: string;
  /** Success wording for the terminal toast — forwarded to the hook. */
  successMessage?: (exportedCount: number) => string;
  /** Wording when the run finished but none of its rows are in the payload —
   *  no count is claimable. Forwarded to the hook. */
  incompleteMessage?: string;
  /** WHICH write this panel performs — forwarded to the hook, which picks the
   *  route. Supplied by the page, the only place that knows the list's source.
   *  Sibling of `actionLabel`: that one decides what the write is CALLED, this
   *  one decides which ROUTE it takes, and both key off the list's `source`. */
  intent?: ExportIntent;
  /** Poll interval (ms) — forwarded to the hook. Default 1500. */
  pollIntervalMs?: number;
  /** Seed the panel selection (carried from the Import-Detail table). EMPTY
   *  means "everything exportable" — see the file docblock. */
  initialSelectedIds?: Iterable<string>;
  /**
   * Cross-page select-all (sub-task 05): when set, the submit rides the
   * filter-scoped write shape (`select_all: true`, no id list) and the
   * confirmation line / CTA / progress speak in the scope's server `total`
   * instead of a reconciled id count. The id-based settle effect is bypassed —
   * there is no seed to reconcile; the server resolves the scope and applies
   * its own per-contact skip rules.
   */
  /**
   * The whole-scope arm, when that is what the page decided: a LANE FLAG plus
   * the filters naming the scope. It carries no counts.
   *
   * It used to carry three — `rowTotal`, `total` and `writable` — and the
   * history is worth keeping, because the prop was where this PR's defect kept
   * reappearing. The three answered three questions (rows the scope holds, rows
   * a run dispatches, rows it writes), and collapsing them was re-introduced
   * and re-fixed twice.
   *
   * All three are gone now, and the reason is better than "nobody read them"
   * (/pr-review iter 6, Agent C N1; step 14 of the plan). `rowTotal` and
   * `writable` genuinely had no reader left once the panel began taking its
   * numbers from `previewQuery`. `total` did have one, and that was the
   * problem: it was the page's client-side `add_resolvable`, and it alone
   * decided whether a select-all run STARTED while the button offering that run
   * was gated on the server's `will_dispatch`. Two numbers answering "is there
   * a run here", which is the exact shape this card exists to remove. The run
   * total now comes from the preview, handed to `exportSelected` at the click.
   */
  selectAllScope?: { filters?: ContactSelectionFilters | null } | null;
  /**
   * The per-contact W2 decisions the recruiter made in the Compare modal, which
   * ride request-time into `w2_decisions` on the write.
   *
   * This panel no longer offers a way to CHANGE them — the four-action control
   * lives in Compare, and the two-option `<select>` that used to sit on each
   * row here disagreed with it (Trello #758). It still carries them, so a
   * decision taken in Compare is honoured by the write it was taken for.
   */
  initialW2Decisions?: Readonly<Record<string, W2Decision>>;
  /** The Thrive search this write joins, named in the confirmation line. Only
   *  the page knows it. Omitted (or `null`) on an `add_to_thrive` run, where the
   *  line names Thrive instead — there is no search to join. */
  searchIdThrive?: string | null;
  /** The search's display name, appended after its id when known. */
  searchName?: string | null;
  /**
   * Take the recruiter to Compare to Thrive. Rendered as the only action in the
   * blocked state, where the Add CTA is disabled and this is the way out.
   *
   * Optional so the component still stands alone; without it the blocked state
   * explains itself and offers nothing, which is honest but a dead end — every
   * real mount passes it.
   */
  onRequestCompare?: () => void;
  /**
   * Is a narrowing filter active behind this scope?
   *
   * Only meaningful with `selectAllScope`, and it decides one clause: "in this
   * list" or "matching your filters". The panel cannot derive it — the filters
   * live on the page and reach the server inside the scope's own `filters`
   * block, never through this component.
   */
  scopeFilterActive?: boolean;
}

/**
 * This file's two plural nouns, in one place.
 *
 * The panel says "people" for a selection and "contacts" for a scope — the
 * nouns differ by surface, so they cannot share one helper with `preAddCopy`'s
 * `people()`, but they were being hand-rolled in three separate template
 * literals here and one of them shipped "all 1 contacts" (/pr-review iter 3,
 * Agent C M5).
 */
function people(n: number): string {
  return `${n} ${n === 1 ? "person" : "people"}`;
}
function contacts(n: number): string {
  return `${n} ${n === 1 ? "contact" : "contacts"}`;
}

/**
 * The one line that replaced the checkbox screen, in Jared's own shape
 * (Trello #729 cl.8): *"You are about to add 28 people to search ID 4414 NYCOO
 * VP Finance"*.
 *
 * ONE FUNCTION, where there were two. `confirmationLine` and
 * `scopeConfirmationLine` existed as a pair only because the two lanes computed
 * their numbers differently — one from loaded rows, one from a scope object —
 * so every time the split between "what will be SENT" and "what will be
 * WRITTEN" was rediscovered, it had to be fixed twice. It was rediscovered
 * four times, and one of those rounds shipped "You are about to add all 0
 * contacts" beside a live button. Both lanes read the same two numbers off the
 * same preview now, so the copies have nothing left to disagree about.
 *
 * THREE STATES, and `writeCount` vs `dispatchCount` is what makes the middle
 * one necessary: rows can travel and none of them be written. A single zero
 * covering both cases said "already in Thrive" about rows that are nothing of
 * the sort, beside a button disabled for a run that is real.
 *
 * IT NO LONGER SAYS WHY. It used to end "— every one has a conflict nobody has
 * decided", true of the only cause the panel could see and false the moment
 * another existed. On app.local it said exactly that about two contacts whose
 * real problem was that nobody had ever compared them. The reasons are the
 * server's to give now, one clause per cause, in the note below; this sentence
 * states the outcome and stops.
 *
 * `scope` names the filter when the cross-page lane is under one. "all 118
 * contacts" with no mention of the filter is the sentence a recruiter is most
 * likely to misread, on the run that writes people they were not looking at —
 * report 2 existed to put that warning on screen. `null` for the explicit lane,
 * which named its own rows.
 */
function confirmationLine(
  writeCount: number,
  dispatchCount: number,
  destination: string,
  scope: string | null,
  inScope: number
): string {
  const these = scope ? `the contacts ${scope}` : "these";
  if (writeCount === 0 && dispatchCount > 0) {
    return `None of ${these} will be added to ${destination} yet.`;
  }
  if (writeCount === 0) {
    // NAMES THE FILTER HERE TOO. A bare "Nothing to add" under an active filter
    // reads as a fact about the whole list, and the recruiter's next move —
    // clear the filter and look again — depends on knowing it is not.
    return scope
      ? `Nothing ${scope} to add to ${destination}.`
      : `Nothing to add to ${destination}.`;
  }
  // "all" ONLY WHEN IT IS ALL. Live on `app.local` with 259 rows of which 4
  // were comparable, this read "You are about to add all 4 contacts in this
  // list" — and the list has 259 (/pr-review iter 11, Agent B, driving the real
  // panel). The sentence promised the right WRITE count and mis-stated the
  // scope it drew from, which is this card's own defect on its headline line:
  // one phrase answering "how many will be written" and "how big is this list".
  //
  // `in_scope` is the server's count of the rows the selection names, and the
  // preview guarantees `in_scope == will_add + Σ not_added`, so comparing the
  // two is free and exact. The note below already names the difference.
  const what = scope
    ? `${writeCount === inScope ? "all " : ""}${contacts(writeCount)} ${scope}`
    : people(writeCount);
  return `You are about to add ${what} to ${destination}`;
}

export function ExportToThrivePanel({
  client,
  importId,
  onTerminal,
  notify,
  actionLabel = "Export Selected to Thrive",
  runningLabel = "Working…",
  successMessage,
  incompleteMessage,
  intent,
  pollIntervalMs,
  initialSelectedIds,
  selectAllScope = null,
  scopeFilterActive,
  initialW2Decisions,
  searchIdThrive,
  searchName,
  onRequestCompare,
}: ExportToThrivePanelProps) {
  const xp = useExportToThrive({
    client,
    importId,
    onTerminal,
    notify,
    successMessage,
    incompleteMessage,
    intent,
    pollIntervalMs,
    initialSelectedIds,
    initialW2Decisions,
    selectAllScope,
  });

  const contactsQuery = useQuery({
    queryKey: ["imports", importId, "contacts"],
    // `page_size: 100` is the endpoint's CAP (`Query(ge=1, le=100)`): ONE
    // request for the FIRST 100 contacts, not "everything" — there is no
    // pagination loop here, so an import over 100 contacts is only partially
    // loaded, which is exactly what the `unexportableLoadedIds` comment below
    // works around. Deliberately NOT `listPageSize()`: that resolver is the
    // per-stage rows-per-page for the three visible lists (20 local / 50 dev),
    // and wiring it in would shrink this first page further while the code
    // still reads as a bulk pull.
    queryFn: () => client.listContacts(importId, { page: 1, page_size: 100 }),
    // NO `refetchInterval` any more. It existed to advance each row's
    // enrichment/export status live while a run was in flight; with the roster
    // gone (Trello #758) there are no rows on screen to advance, and the
    // progress bar reads the job-status poll instead. The terminal-effect
    // invalidate in `useExportToThrive` still refreshes the table behind this
    // panel, which is where per-contact state is now read.
  });

  const rows = useMemo(() => contactsQuery.data?.items ?? [], [contactsQuery.data]);

  // Rows we have LOADED and know are not exportable. Only these may be dropped
  // from the seed: `listContacts` fetches page 1 (page_size 100) and the table
  // behind this panel selects across pages, so an id absent from `rows` is
  // "not loaded", NOT "not exportable". Dropping those would silently shrink a
  // run to its first hundred contacts.
  const unexportableLoadedIds = useMemo(
    () => new Set(rows.filter((r) => !isExportable(r)).map((r) => r.import_contact_id)),
    [rows]
  );

  /**
   * Settle the selection ONCE, when the contacts land, and never again.
   *
   * ONE job: drop anything the seed carried that cannot be written, so the
   * count in the confirmation line is the count that gets submitted.
   *
   * It used to have a second job — seeding an EMPTY open with "everything
   * exportable" — and that job was the bug. The set it seeded from came out of
   * `contactsQuery`, which fetches `page: 1, page_size: 100` and does not
   * paginate, so a 1,300-contact list opened from the Search-card CTA (which
   * requires no selection) seeded 100 ids and wrote 100 contacts. The comment
   * on `unexportableLoadedIds` below has always named this exact hazard —
   * "silently shrink a run to its first hundred contacts" — and guarded only
   * the OTHER branch.
   *
   * The empty open is now a SCOPE, decided by `ImportDetailPage` (`addScope`)
   * before this panel mounts, because it is the only component that knows
   * `totalContacts` and the selection mode. It arrives as `selectAllScope` and
   * this effect returns early on it, exactly as it already did for cross-page
   * mode. Paginating here would not have worked: `contact_ids` is capped at 200
   * server-side and ~204 ids cross the ALB WAF's ~8 KB limit.
   *
   * Once, deliberately. `contactsQuery` re-polls during a run and rows flip to
   * `exported` as the worker finishes them, so a live reconciliation would eat
   * the in-flight selection row by row; and `useExportToThrive` clears the
   * selection on terminal, which a live re-seed would immediately undo — the
   * panel would re-arm itself for a second run of the same contacts.
   */
  const settledRef = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs exactly once on the first successful contacts load (settledRef); `xp` is a fresh object each render and listing it would not change when this fires
  useEffect(() => {
    // No settle in cross-page mode: there is no id seed to reconcile and no
    // empty-open to fill — the scope is the filter claim, resolved server-side.
    // Seeding "everything exportable" here would quietly re-arm the id lane
    // the submit deliberately does not use.
    if (selectAllScope) return;
    if (settledRef.current || !contactsQuery.isSuccess) return;
    settledRef.current = true;
    const next = [...xp.selectedIds].filter((id) => !unexportableLoadedIds.has(id));
    if (next.length === xp.selectedIds.size) return;
    xp.clearSelection();
    xp.selectMany(next);
  }, [contactsQuery.isSuccess, unexportableLoadedIds]);

  // `unwritableLoadedIds`, `pendingCount` and `dispatchCount` used to be
  // computed here. All three re-derived the WRITE POLICY from a page of loaded
  // rows; the server answers both questions now, over the whole selection, by
  // running the policy itself (`previewQuery` above).
  //
  // What they got wrong was not arithmetic. The panel loads ONE page while the
  // table behind it selects across pages, so every id outside that page had to
  // be counted as writable — "not loaded" is not "not writable" — which made
  // the sentence an over-promise by construction on any selection wider than a
  // page. The preview has no such blind spot.
  //
  // The DISTINCTION they drew survives, because it was the right one: one
  // number answers "is this a run" and the other "what will it write", and they
  // diverge on a ticked conflict with no decision. They are `will_dispatch` and
  // `will_add` now, computed once, server-side.

  /**
   * THE LAST COMPARE — read, never started.
   *
   * `GET /imports/{id}/compare` returns the persisted `dedup_status` of the
   * last run, so the numbers survive a reload and cost no second dry run. This
   * is the first caller in the app to read it without a `POST` in front:
   * `useCompareToThrive` always enqueues a fresh run on mount, and claiming a
   * run REPLACES the stored blob.
   *
   * `retry: false` because the interesting failure is a 404 — "no compare run
   * has been started" — and retrying it three times only delays the state that
   * answers it. `refetchOnMount: "always"` because a recruiter who runs Compare,
   * closes it and opens Add must see the run they just watched; the same
   * mount-fetch discipline `PreCompareConfirmation` uses for its coverage
   * counts, and for the same reason: this component only exists while open.
   */
  /**
   * `errorUpdateCount` at the last successful read — the zero point for
   * "consecutive failures". See the bound inside `refetchInterval`.
   *
   * `null` until this panel's first `refetchInterval` callback, and seeded
   * there rather than at 0: the
   * counter lives on the CACHE ENTRY, which outlives this component by
   * `gcTime`, while the ref is per-instance. Starting at 0 meant a panel
   * reopened after some failures computed a budget already spent and refused to
   * retry at all (/pr-review iter 5, Agent C). Seeding from the entry makes the
   * budget "failures since this panel opened", which is what it claims to be.
   */
  const readErrorBaselineRef = useRef<number | null>(null);
  const compareQuery = useQuery({
    queryKey: ["imports", importId, "compare"],
    queryFn: () => client.getCompare(importId),
    retry: false,
    staleTime: 0,
    refetchOnMount: "always",
    /**
     * Poll ONLY while a compare is in flight.
     *
     * That branch renders no button — the way out is to wait — so without this
     * it was the one blocked state with neither an action nor a way to leave:
     * `refetchOnWindowFocus` is false globally (`main.tsx`), nothing invalidates
     * this key, and the panel would still say "wait for it to finish" ten
     * minutes after it finished. Closing and reopening was the only exit, and
     * nothing on screen said so (/pr-review iter 1, Agent C I1).
     *
     * Keyed off the QUERY's own data rather than `compareBlockReason`, which is
     * declared below and would be a use-before-define; the two agree because
     * `running` is exactly "terminal statuses absent".
     */
    refetchInterval: (query) => {
      // Seeded on this panel's FIRST callback, whatever the status, and without
      // an offset. `QueryObserver#onSubscribe` runs this synchronously at mount
      // — before the mount fetch resolves — so on a cold start the count here is
      // 0, and on a reopen into an inherited error state it is whatever the
      // cache entry already carried. Either way it is the zero point, and the
      // failure that follows is this panel's first.
      //
      // The previous version seeded `count - 1` inside the error branch, on the
      // reasoning that the read which just failed belonged to this panel. That
      // holds on a cold start and is one low on a reopen, where the callback had
      // already run before any read of ours failed — 4 retries where the
      // docblock promises 5 (/pr-review iter 6, Agent C).
      if (readErrorBaselineRef.current === null) {
        readErrorBaselineRef.current = query.state.errorUpdateCount;
      }
      // WITH `retry: false`, THIS IS THE RETRY. A cold-start failure has no
      // `data`, so returning false here left the recruiter on copy that named a
      // transient fault with nothing on screen to try again with — the dead end
      // `running` had just stopped being (/pr-review iter 2, Agent C N2).
      if (query.state.status === "error") {
        // RETAINED DATA WINS HERE TOO. Both stop conditions below used to fire
        // regardless of what we already held, while `compareBlockReason` had
        // been taught the opposite — so a failed poll over a retained `running`
        // payload left the reason at "running" with no poll, no `Try again`
        // (gated to `unreadable`) and no `Compare to Thrive` (gated off
        // `running`): a dead panel, which is iter-1's I1 in a narrower window
        // (/pr-review iter 4, Agent C I1). A run we are watching keeps being
        // watched; only a failure with nothing behind it may stop.
        const consecutiveFailures = query.state.errorUpdateCount - readErrorBaselineRef.current;
        const watching = query.state.data?.status;
        if (watching !== undefined && watching !== "succeeded" && watching !== "failed") {
          // STILL BOUNDED, and by the SAME bound as below — this branch buys no
          // extra budget, which an earlier version of this comment claimed it
          // did (/pr-review iter 5, Agent A). Its one effect is to skip the 404
          // short-circuit: a held run keeps being polled even if a poll 404s,
          // because the run is real and we are watching it. The first version
          // returned the interval unconditionally here, which fixed the dead
          // panel by polling a failing endpoint forever — the very thing the
          // bound exists to stop. Past it, `Try again` is the way back.
          return consecutiveFailures >= READ_RETRY_LIMIT ? false : (pollIntervalMs ?? 1500);
        }
        // A 404 is the one answer that cannot change — no compare run has ever
        // been started, and the commonest way to open this panel. Re-reading it
        // polls a permanent answer (/pr-review iter 3, Agent A §3a).
        if (isNotFound(query.state.error)) return false;
        // BOUNDED, because the first version was not: a `GET /compare` that
        // keeps 5xx-ing got ~40 requests a minute from every open panel, with
        // no cap (/pr-review iter 3, Agent C I1).
        //
        // Counted against a baseline rather than off either built-in counter,
        // because neither means "consecutive failures" (Agent C I2, and the
        // test that then caught the replacement): `errorUpdateCount` is never
        // reset by the reducer's success branch, so it is a LIFETIME count on a
        // cache entry outliving the panel by `gcTime`; `fetchFailureCount` is
        // reset at the START of every fetch, so with `retry: false` it never
        // exceeds 1 and bounds nothing. The baseline moves on each success, so
        // this is failures since the last read that landed.
        return consecutiveFailures >= READ_RETRY_LIMIT ? false : (pollIntervalMs ?? 1500);
      }
      if (query.state.status === "success") {
        readErrorBaselineRef.current = query.state.errorUpdateCount;
      }
      const status = query.state.data?.status;
      if (status === undefined) return false;
      return status === "succeeded" || status === "failed" ? false : (pollIntervalMs ?? 1500);
    },
  });
  const compare = compareQuery.data;

  /**
   * No usable numbers ⇒ no write (Lucas, 2026-09-09).
   *
   * Adding to Thrive goes through Compare, so a panel with nothing to show is
   * not a panel that should quietly write anyway. FOUR distinct causes, told
   * apart because they need different sentences and only one of them means
   * "wait": a 404 (never compared), a `failed` run, a run in flight (which has
   * already blanked the stored blob, so there is genuinely nothing to read), and
   * a read we could not make at all — which is OUR failure, not the recruiter's,
   * and must not be reported as theirs.
   *
   * This is the ONE place the v37 rule bends. A conflict still never disables
   * the write; an import with no successful compare on record now does. The
   * gate is per-IMPORT — it never inspects which contacts the stored run
   * covered, so a mostly-uncompared selection still writes with a warning.
   */
  const compareBlockReason: "never" | "failed" | "running" | "unreadable" | null =
    compareQuery.isPending
      ? // Not yet an answer. Handled by its own branch below rather than folded
        // into one of the four reasons: "we have not looked yet" is not "there
        // is nothing", and rendering either the blocked warning or a stats block
        // of zeroes while the read is in flight states something we do not know.
        null
      : compareQuery.isError && compare === undefined
        ? // ONLY a 404 means "never compared". `retry: false` makes a single 500,
          // a timeout or an edge 403 enough to land here, and calling that "these
          // contacts have not been checked against Thrive" is a false statement
          // about the data — one that then offers a Compare button and invites a
          // fresh full dry run, which is the cost this whole design exists to
          // avoid (Trello #766: 1,777 contacts in 23.6 s since PR #844 folded the
          // matcher — 191.4 s before it). `ImportsApiError`
          // carries the status; anything that is not a 404 is US failing to read,
          // not the recruiter failing to compare (/pr-review iter 1, Agent A 1).
          isNotFound(compareQuery.error)
          ? "never"
          : "unreadable"
        : // RETAINED DATA WINS over a failed refetch, matching the discipline
          // `refetchInterval` already applies. Reading `isError` first meant one
          // transient poll failure mid-run flipped a compare we were watching to
          // `unreadable` — losing the state AND, while the stuck timer existed,
          // resetting it, so a flaky endpoint could keep the escape from ever
          // firing (/pr-review iter 3, Agent C I2). A failure with data behind
          // it is a failed refresh, not an unreadable compare.
          compare?.status === "succeeded"
          ? null
          : compare?.status === "failed"
            ? "failed"
            : "running";
  const blocked = compareBlockReason !== null;

  /**
   * What the run would actually do — asked of the server, for THIS body.
   *
   * Every number this panel states comes from here. They used to be derived
   * client-side from a page of loaded rows, which is how the panel came to
   * promise writes the worker never made: the FE was re-deriving the write
   * policy and the two derivations disagreed. The endpoint runs
   * `resolve_export_plan` itself, so there is only one.
   *
   * KEYED ON THE BODY, with `contact_ids` SORTED. TanStack's `hashKey` sorts
   * object keys but is order-sensitive on arrays, so an unsorted id list makes
   * re-ticking the same rows in a different order look like a different
   * question and refetches for nothing.
   *
   * The panel mounts only while open and has NO checkboxes (Trello #729 cl.8)
   * — the selection is settled before it opens — so this fires once per open,
   * not per click. `refetchOnMount: "always"` for the same reason `compareQuery`
   * has it: a recruiter who just decided a conflict must not be shown the tally
   * from before they decided.
   */
  const previewQuery = useQuery({
    queryKey: [
      "imports",
      importId,
      "add-preview",
      {
        ...xp.addRequestBody,
        contact_ids: xp.addRequestBody.contact_ids
          ? [...xp.addRequestBody.contact_ids].sort()
          : undefined,
      },
    ],
    queryFn: () => client.previewAdd(importId, xp.addRequestBody),
    // Nothing to preview behind the compare gate (the panel is showing the
    // blocked state, and a run cannot start from there anyway), and nothing to
    // preview WHILE a run is in flight — the progress bar has replaced the
    // sentence, and it would be a wasted round trip on every poll.
    //
    // THE GATE USED TO BE `xp.exportTotal === 0`, AND THAT WAS A BUG
    // (/pr-review iter 7, Agent C N4). `exportTotal` is never reset, so the
    // query stayed disabled for the rest of the panel's life while TanStack
    // kept the cached tally — and on the select-all lane the key is
    // `select_all` plus filters, which a run does not change, so nothing
    // evicted it. The comment here claimed success "clears the selection, so
    // the body recomputes to an empty one"; that is true only of the explicit
    // lane, and it was the whole basis for calling the stale tally harmless.
    //
    // What it cost: after a terminal run, `pendingCount` and `dispatchCount`
    // still held the PRE-RUN numbers, every term of the CTA's `disabled`
    // expression was false, and a click dispatched the entire scope a second
    // time under the first run's denominator. The operator's rule — a
    // selection that would write nothing disables the CTA — was being asked
    // about a tally that predated the run, so the one state it exists for is
    // the state it could not see.
    //
    // Gating on `isExporting` instead re-enables the query once the run is
    // terminal. The re-read is the fix, not an extra guard on the button:
    // already-exported rows fall out of `_export_resolvable_condition`, so
    // `will_add` comes back 0 and the existing rule disables the CTA by
    // itself. It also leaves a failed run RETRYABLE, which a blanket
    // "disable after any run" would not — **on the select-all lane**. The
    // explicit lane clears its selection on every terminal, failed included
    // (`useExportToThrive.ts:729`), so there is nothing left to retry there.
    // That is pre-existing and identical on `origin/dev`; the sentence just
    // used to claim both lanes (/pr-review iter 11, Agent C).
    // AND ONLY WHEN THE BODY ASKS SOMETHING. An explicit selection of nothing
    // has nothing to preview: `_resolve_selection` reads that body as naming no
    // rows, so the answer is zero before the request goes out (/pr-review iter
    // 10, Agent C N10, closing an older m19 with it).
    //
    // This is what the removed `runSettledAt === 0` term was really covering on
    // the explicit lane, and covering by accident. A successful run CLEARS the
    // selection, so the panel POSTed a preview for `contact_ids: []` — and when
    // that POST failed it rendered "we could not check what this run would do"
    // one line above "Added 1 of 1", which is iteration 5's defect returning.
    //
    // The reviewer's sharper point is why the guard belongs HERE and not on the
    // warning: when that same read SUCCEEDS the button is equally dead, because
    // the selection is empty. The empty selection is the cause; the failed read
    // was a symptom. Not asking drops the round trip and the warning together.
    enabled:
      !blocked &&
      !compareQuery.isPending &&
      !xp.isExporting &&
      (xp.addRequestBody.select_all === true || (xp.addRequestBody.contact_ids?.length ?? 0) > 0),
    refetchOnMount: "always",
    retry: false,
  });
  const preview = previewQuery.data;
  /**
   * A tally we could not read is not a tally of zero.
   *
   * On failure the panel REFUSES rather than falling back to client-side
   * arithmetic. That fallback is the very derivation this endpoint replaces and
   * would fail in the most expensive way available: silently, with numbers that
   * look right. Not hypothetical either — `w2_decisions` is uncapped and the
   * ALB refuses bodies over ~8 KB with a CORS-less 403 the browser reports as a
   * bare `TypeError`, so an edge refusal that used to happen only on submit can
   * now happen at panel-open.
   */
  // `xp.exportTotal === 0` USED TO BE A TERM HERE AND IS GONE, and the history
  // is worth four lines because five rounds argued about it and most of the
  // arguments — including all of mine — were wrong.
  //
  // Added in iteration 5 to stop the warning rendering beside a run that had
  // just succeeded. Found pinned by no test in iteration 6. Defended on
  // reasoning twice; both readings were refuted by measurement, and one of them
  // — "the block is not rendered at all post-terminal" — was falsified by a
  // reviewer forcing the guard true and finding the element on screen, which is
  // recorded a few lines below and which an earlier version of THIS paragraph
  // contradicted (/pr-review iter 11, Agent B).
  //
  // What replaced it is `!preview || previewQuery.isError`, which catches a
  // case the old term could not: a failed refetch on the select-all lane
  // RETAINS the previous tally, so `!preview` is false while the numbers are
  // stale. That term is pinned.
  //
  // No green-count is quoted here. One was, taken at a head that no longer
  // exists, and a count in a comment is a fact with an expiry date nobody sets.
  //
  // What DOES still consume this value is the CTA gate below: with the preview
  // unreadable there is no tally to run against, so the button refuses. That is
  // its whole job now.
  const previewUnavailable =
    // NOT WHILE THE RUN IS HAPPENING. "We could not check what this run would
    // do" above "Exporting… 9 of 9" is noise at best.
    //
    // LOAD-BEARING, and the first comment here said it was not (/pr-review
    // iter 10 Agent A raised it, iter 11 Agent C reproduced it). I added this
    // term unable to build the case for it and labelled it defence; the case
    // exists.
    //
    // It needs a WARM cache. Reopening the panel serves the stored tally on the
    // first frame, so the CTA is live immediately while `refetchOnMount`
    // starts a fresh read; click inside that round trip and the run begins with
    // a fetch in flight. When that fetch fails, TanStack RETAINS the data — so
    // `!preview` is false and cannot catch it — while `isError` goes true and
    // `isPending` false. Without this term the panel prints "we could not check
    // what this run would do" directly above `Exporting… 0 of 1`.
    //
    // The probe I wrote beside it only exercised the `!preview` arm, which is
    // why deleting the term left the whole panel file green and let me believe
    // my own comment. There is a test for the real path now.
    !xp.isExporting &&
    // NOT AFTER A RUN HAS SETTLED. "We could not check what this run would do"
    // is a sentence about a run that has not happened yet; beside one that just
    // finished it is a failure message with nothing failing behind it.
    //
    // This is the guard `exportTotal === 0` was approximating for three rounds.
    // I deleted that term in iteration 7 on the argument that the block is not
    // rendered post-terminal — the fourth wrong explanation of it, and Agent A
    // falsified it in iteration 8 by forcing the guard true and finding the
    // element on screen. The explicit lane is where it shows: the run clears
    // the selection, so the query KEY changes, `preview` really is undefined,
    // and the warning rendered beside a successful run.
    //
    // `runSettledAt` was the guard here for one round and is gone; the
    // expression reads `!preview || previewQuery.isError` now, which catches a
    // case it could not — a failed refetch on the select-all lane RETAINS the
    // previous tally, so `!preview` is false while the numbers are stale.
    //
    // EXCEPT ON THE ERROR ARM (/pr-review iter 9, Agent A). Suppressing after a
    // run silences the one state where "we could not check what this run would
    // do" is TRUE: the post-run read genuinely failed. And it fails closed —
    // `retry: false`, so nothing re-reads — leaving a permanently disabled
    // primary button with no sentence and no way out. The recruiter is told
    // nothing about a button they cannot press.
    //
    // The iteration-5 defect this guard exists for was the warning appearing
    // beside a run that SUCCEEDED, on a tally that had merely not been
    // refetched. An actual error is the opposite case, so it keeps its message.
    !blocked &&
    !compareQuery.isPending &&
    !previewQuery.isPending &&
    // SETTLED, not merely absent. `!preview` is true for the whole first fetch
    // too, so without this the panel OPENED on "we could not check what this
    // run would do" every single time and then corrected itself
    // (/pr-review iter 5, Agent C). A failure message shown while nothing has
    // failed teaches the recruiter to ignore the one that means something.
    //
    // OR THE READ FAILED, which is not the same as having no tally and is the
    // case `!preview` alone cannot see (/pr-review iter 9, Agent A). On the
    // select-all lane the query key does not change across a run, so a failed
    // post-run refetch leaves the PREVIOUS tally in the cache: `preview` is
    // defined, `!preview` is false, and the panel says nothing — while
    // `tallyPredatesRun` has correctly disabled the button. A permanently dead
    // primary control with no explanation and no retry (`retry: false`).
    //
    // `runSettledAt === 0` sat here for a round as the guard and is gone. It
    // was measured redundant: every state it suppressed is one where `preview`
    // is defined anyway. This is the fifth time that term has been examined and
    // the first time the examination produced a term that earns its place.
    (!preview || previewQuery.isError);
  /**
   * IS THE TALLY WE ARE HOLDING OLDER THAN THE RUN?
   *
   * The preview is a cached query whose key does not change across a select-all
   * run, so after the run TanStack serves the PRE-RUN tally while it refetches,
   * and keeps serving it permanently if that refetch fails (`retry: false` plus
   * retain-on-error). Numbers from before the run then drove the CTA, and a
   * click re-dispatched the entire scope. Found independently by all three
   * reviewers in iteration 8 after the iteration-7 fix closed only the case
   * where the re-read RETURNS.
   *
   * `dataUpdatedAt` is the timestamp of the last SUCCESSFUL fetch, so this is
   * true both while a refetch is in flight and after one has failed. It also
   * covers the case a plain `isFetching` term missed.
   */
  const tallyPredatesRun =
    // `dataUpdatedAt === 0` was a second disjunct here and is gone: inside
    // `runSettledAt > 0` it is subsumed by the comparison beside it, since 0 is
    // less than any positive timestamp (/pr-review iter 11, Agent C). Dead by
    // algebra rather than merely unpinned.
    xp.runSettledAt > 0 && previewQuery.dataUpdatedAt < xp.runSettledAt;
  /** What the worker will WRITE — the sentence's promise. */
  const pendingCount = preview?.will_add ?? 0;
  /**
   * What the request will SEND: the progress bar's denominator, and ONE of the
   * CTA's two count terms.
   *
   * THAT FIRST CLAUSE WAS FALSE WHEN IT WAS WRITTEN, and is true now. Agent C
   * checked it in iteration 6 (N3): this value had two readers, the CTA gate
   * and the sentence, and neither was the bar — `ProgressBar`'s `max` is
   * `xp.exportTotal`, which the select-all lane seeded from the page's own
   * `selectAllScope.total`. The N1 fix in the same round routed it here: the
   * click now passes this number to `exportSelected`, which sets `exportTotal`
   * from it. Kept as a note rather than silently corrected, because a docblock
   * that described an intention instead of the code is how the two numbers
   * stayed apart for six rounds.
   *
   * THIS DOCBLOCK SAID "the CTA's gate", SINGULAR, AND THE SAME HEAD REVERSED
   * THAT (/pr-review iter 5, Agent A). It used to be the whole gate, on the
   * iteration-3 rule that a selection of undecided conflicts must stay
   * runnable. The operator reversed that on 2026-09-17 because such a run
   * became a no-op, so the button now also requires `pendingCount > 0` — see
   * `ctaOffersARun`, which carries all of the button's terms and their
   * reasoning. `disabled` is `!ctaOffersARun` and nothing else; it re-listed
   * the terms until /pr-review iter 10, Agent B found the two copies drifting.
   */
  const dispatchCount = preview?.will_dispatch ?? 0;

  /**
   * A `running` compare that never lands is NOT given an escape here, and that
   * is a decision rather than an omission (/pr-review iter 3, Agent C C1).
   *
   * Iteration 2 added one: past ~30 s the copy flipped to "this may have
   * stopped" and offered `onRequestCompare`, on the reasoning that a re-run
   * RECLAIMS an abandoned job. The server disagrees for the first hour —
   * `_STALE_IN_FLIGHT_AFTER = timedelta(minutes=60)` (`imports/repository.py`),
   * so between 30 s and 60 min the claim is refused and `try_claim_dedup_status`
   * hands back the existing job idempotently. Nothing duplicates, but "running
   * it again will pick it up" is false for 59.5 of those 60 minutes. THAT leg
   * carries the decision on its own.
   *
   * The second leg has since been cut, and saying so matters more than keeping
   * a tidy argument: this branch was rebased onto PR #844, which folded the
   * matcher and took the same 1,777-contact compare from 191.4 s to 23.6 s. At
   * 23.6 s a 30-second nudge would NOT call a healthy compare dead, so the
   * "most of its run being called dead" half of the original reasoning is no
   * longer true. The escape stays deleted on the reclaim window alone
   * (/pr-review iter 8, Agent C).
   *
   * That constant's own docblock rejects this approach for this problem in as
   * many words: *"The answer to the wait is NOT a smaller number here… It is
   * telling the user what is happening: surfacing the `progress` block the
   * worker already writes (Trello #748), so a long run reads as a long run
   * instead of a dead button."* `CompareResult` carries no `progress` field
   * today, so the honest fix is a wire change this frontend-only PR is not.
   *
   * What remains is the poll, which is the part that was actually missing: a
   * compare that DOES land unblocks the panel on its own.
   */

  /**
   * When the compare lands, refresh the contacts it just reclassified.
   *
   * Before the poll existed, the only route past a `running` compare was close
   * + reopen, which remounted and refetched BOTH queries. The poll opened a
   * route that refetched only one, leaving `rows` — and so
   * `unexportableLoadedIds` and `pendingCount` — on the pre-compare fetch. A
   * row the compare just called `duplicate` would still be counted exportable
   * and still travel in `contact_ids`; the server skips it, so nothing is
   * corrupted, but the sentence over-promises, which is the one thing this
   * panel exists to stop (/pr-review iter 2, Agent C N3).
   *
   * `settledRef` has already latched by then, so the seed is deliberately NOT
   * re-run — re-seeding mid-session is the behaviour its docblock forbids. What
   * this fixes is the NUMBER the panel states, not the scope it submits.
   */
  const compareStatus = compare?.status;
  const wasRunningRef = useRef(false);
  useEffect(() => {
    const terminal = compareStatus === "succeeded" || compareStatus === "failed";
    if (wasRunningRef.current && terminal) {
      void contactsQuery.refetch();
    }
    wasRunningRef.current = compareStatus === "queued" || compareStatus === "running";
    // `contactsQuery.refetch` is listed to satisfy the exhaustive-deps rule and
    // is stable across renders, so it never re-fires this. The ONLY thing that
    // does is `compareStatus` — which is what the effect is about, and worth
    // saying because the dep list otherwise reads as if both mattered
    // (/pr-review iter 3, Agent C M2).
  }, [compareStatus, contactsQuery.refetch]);

  // `coverageLine` lived here and is gone. It read
  // `contactsQuery.data.counts.review` — an UNFILTERED, page-1 query — so it
  // answered about the whole IMPORT while the run was about a SELECTION, and
  // its own docblock admitted it over-reported in one direction and
  // under-reported in the other (a never-compared row that failed a write
  // counts under `failed`, not here). On app.local it said "257 contacts were
  // never compared" beside a selection of two.
  //
  // The same question is answered exactly now, scoped to the actual selection,
  // by the `not_compared_no_thrive_check` cause in the preview.

  // Where the write lands, in the recruiter's words. `add_to_thrive` (a Company
  // Map) joins no search by design — `export_worker/service.py` passes
  // `search_id = None` — so naming one there would promise the opposite of what
  // runs. An add-to-search list with no linked search cannot reach this panel
  // (the API 409s), but the fallback keeps the sentence true if it ever does.
  /**
   * THE 200-ID CAP, which used to live one screen earlier.
   *
   * `contact_ids` is capped at `EXPORT_CHUNK_SIZE` server-side, and ~204 ids
   * cross the shared ALB WAF's ~8 KB body limit as a CORS-less 403 that reaches
   * the browser as a bare `TypeError: Failed to fetch`. So an over-cap run has
   * to be refused before the request, not after it.
   *
   * It was enforced in `PreAddConfirmation`, which is no longer on every route
   * into Add: since the scope dialog only opens for a genuine page-vs-import
   * choice, a cross-page tick-by-tick selection of 210 rows now reaches this
   * panel having passed no guard at all. The cap moves here, where every route
   * does arrive.
   *
   * READS THE PAYLOAD, not the promise. `pendingCount` subtracts undecided
   * conflicts from what the sentence claims, but those ids still travel —
   * 210 selected with 15 conflicts promises 195 and posts 210, and a guard on
   * the promise waves exactly that through.
   */
  /**
   * WHY THIS PANEL'S NUMBER IS SMALLER THAN THE CTA THAT OPENED IT.
   *
   * The footer CTA counts ROWS ("Add Selected (all 1300)"); the sentence above
   * counts WRITES (842). Both are right, and a recruiter reading them on
   * adjacent screens has no way to know they answer different questions — which
   * is report 1's original complaint.
   *
   * It used to be answered by `preAddScopeExcludedNote` in the scope dialog,
   * which stood on every route into Add. It no longer does: the dialog was
   * narrowed to the single page-vs-import question, so the banner route and the
   * Search-card route now reach this panel having seen the CTA's number and
   * nothing reconciling it. The over-cap refusal and the zero-writable sentence
   * were moved here when that happened; this one was missed, and a reviewer
   * found it by tracing the routes rather than by reading the commit that
   * claimed the move was complete.
   *
   * Same function as the dialog's, same two causes, so the two surfaces cannot
   * drift into describing the gap differently.
   */
  /**
   * Why part of the selection will not be added — one clause per cause.
   *
   * Replaces `scopeGapNote`, which took the only two numbers it could SUBTRACT
   * out of a scope object — "rows a run never sends" and "rows it sends and
   * does not write" — and could therefore name two causes, both inferred. The
   * server names all six, from the policy that produces them, so a
   * never-compared row stops being reported as an undecided conflict.
   *
   * It covers BOTH lanes now. `scopeGapNote` was `selectAllScope`-only: an
   * explicit selection got no gap note at all, so the shortfall between what
   * the recruiter ticked and what would be written went unexplained.
   *
   * `?? []` because `not_added` carries a server-side default and is therefore
   * OPTIONAL on the wire rather than merely possibly-empty — typescript caught
   * that, and absent reads as "no causes", the same way the server's own
   * `default_factory` reads it.
   */
  const notAddedNote = preview
    ? exportNotAddedNote(preview.not_added ?? [], { omit: COMPARE_LINE_CAUSES })
    : null;

  /**
   * ROWS THE RUN LEAVES OUT FOR WANT OF A RECENT COMPARE (Trello #823,
   * sub-task 04). The Add is not blocked: the server sends only the rows
   * compared within `compare_max_age_hours` (24 by default, a per-stage
   * setting) and leaves the others "Not added yet". Both counts are the
   * preview's own `not_added` entries, from the same plan the send uses, so no
   * second count exists to disagree with it. They are not part of
   * `pendingCount`, so the confirmation line beside them already promises
   * only what will be written. Not folded into `compareBlockReason`, which is
   * about the stored run and is known before the preview is asked.
   */
  const notAddedCount = (reason: AddPreviewSkipReason) =>
    preview?.not_added?.find((c) => c.reason === reason)?.count ?? 0;
  const neverComparedCount = notAddedCount("not_compared_no_thrive_check");
  const staleCompareCount = notAddedCount("compare_too_old");
  const needsCompare = !blocked && !xp.isExporting && neverComparedCount + staleCompareCount > 0;
  /**
   * The people the recruiter meant to add: what the run will write plus the
   * rows it leaves out on either cause, so the line reads "3 of 5" against
   * the selection, not against what remains (/pr-review iter 1, C M2).
   */
  const meantToAddCount = pendingCount + neverComparedCount + staleCompareCount;
  /**
   * Every state in which the panel will not write, for the sentences that
   * would otherwise promise a write. Gated on `blocked` alone, the
   * confirmation line said "you are about to add 16" beside a disabled button
   * and the line saying why it is disabled. The Compare line is not a hold:
   * the rest of the selection still goes.
   */
  const held = blocked;

  const tooManySelected = !selectAllScope && xp.selectedIds.size > MAX_EXPLICIT_ADD_SELECTION;
  const tooManyNoteId = "export-too-many-note";

  /**
   * THE BUTTON AND THE SENTENCES THAT EXPLAIN IT SHARE ONE SOURCE.
   *
   * They were gated separately — the CTA on its own expression, the
   * confirmation sentence and the not-added note on `exportTotal === 0` — and
   * the two disagreed after a run that FAILED: the retry button went live
   * while both explanations were gone, so the panel offered an action it said
   * nothing about (/pr-review iter 9, Agent C N9).
   *
   * Defined once and consumed by all three, so they cannot drift apart. It is
   * the rule this whole card exists for, applied to the panel's own markup.
   */
  const ctaOffersARun = !(
    blocked ||
    // NOT the Compare line (sub-task 04): it leaves rows out, it does not
    // stop the run. When nothing recent is left, `pendingCount === 0` below
    // disables the button and the line stays to say why.
    compareQuery.isPending ||
    tooManySelected ||
    // ONE TERM FOR BOTH LANES. It read `selectAllScope.total` on one and a
    // client-side count on the other, which is two ways to answer "is there a
    // run here" and therefore two ways to be wrong. `will_dispatch` is the
    // server's answer for both.
    dispatchCount === 0 ||
    // NOTHING WILL BE WRITTEN, SO THERE IS NOTHING TO RUN (operator,
    // 2026-09-17). This REVERSES iteration 3, which gated on dispatch rather
    // than writes so a selection of undecided conflicts stayed runnable. What
    // changed is that the run became a no-op: every zero-write outcome leaves
    // the rows exactly as they were. The iteration-3 concern was a W2 row the
    // recruiter had DECIDED, and that case is unaffected because the decision
    // makes `will_add` non-zero. A button whose only effect is a wasted round
    // trip is the worse of the two failures.
    pendingCount === 0 ||
    // A tally we could not read is not permission to run.
    previewUnavailable ||
    // NEITHER IS A TALLY OLDER THAN THE RUN (/pr-review iter 8, all three
    // reviewers). Re-enabling the preview post-terminal only helps when the
    // re-read RETURNS; TanStack serves the cached pre-run tally while
    // refetching, and permanently if that refetch fails.
    tallyPredatesRun ||
    xp.isExporting
  );

  const destination =
    intent === "add_to_thrive" || !searchIdThrive
      ? "Thrive"
      : `search ID ${searchIdThrive}${searchName ? ` ${searchName}` : ""}`;

  return (
    <div data-testid="export-to-thrive" className="flex flex-col gap-4 p-6">
      <header className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-h2 text-text">{actionLabel}</h2>
          {/* The whole confirmation, in one sentence. What stood here was
              "Re-export is idempotent on contact identity and additive on annex
              fields — nothing gets duplicated or overwritten", deleted on
              2026-08-27 (Trello #729 cl.8, Jared: "I would just get rid of
              that"): it explained an implementation guarantee to someone who
              only wants to know how many people are about to move, and it did
              so in three words no recruiter uses.

              Hidden while the run is in flight: "you are ABOUT to add" stops
              being true the moment the POST goes out, and the progress bar
              below answers the same question from then on. It comes BACK once
              the run is over if the CTA is offering another one, which is what
              `ctaOffersARun` in the condition below is for — a live button
              with nothing beside it was the defect that condition closed. */}
          {(xp.exportTotal === 0 || ctaOffersARun) && !held && !compareQuery.isPending ? (
            <p data-testid="export-confirmation-line" className="text-body-sm text-text-secondary">
              {/* Cross-page mode speaks in the scope's total, with the skip
                  rules stated instead of pre-reconciled: the client never
                  loads the full set, so the server applies them per contact —
                  the sentence must not promise per-row arithmetic it cannot
                  do. */}
              {confirmationLine(
                pendingCount,
                dispatchCount,
                destination,
                selectAllScope
                  ? scopeFilterActive
                    ? "matching your filters"
                    : "in this list"
                  : null,
                preview?.in_scope ?? 0
              )}
            </p>
          ) : null}
          {/* ONE LINE FOR BOTH CAUSES, never compared and compared too
              long ago, counted together (Lucas, 2026-10-29): one Compare
              clears both. Quiet, in the voice of the notes below it, and
              saying who the run leaves out (sub-task 04). */}
          {needsCompare && (
            <p data-testid="export-needs-compare" className="text-body-sm text-warning-ink">
              <span aria-hidden="true">⚠ </span>
              {exportNeedsCompareNote(neverComparedCount + staleCompareCount, meantToAddCount)}
            </p>
          )}
          {/* THE WAY OUT OF THAT LINE: a button like Add, under the line it
              answers, while Add keeps its place on the right
              (Lucas, 2026-10-29). The same launch and the same words as
              `Compare to Thrive`, whichever cause it answers. */}
          {onRequestCompare && needsCompare && (
            <div className="mt-2">
              <Button data-testid="export-run-compare" onClick={onRequestCompare}>
                {EXPORT_RUN_COMPARE_CTA}
              </Button>
            </div>
          )}
          {/* THE SAME GUARD AS THE SENTENCE ABOVE, because this note explains
              that sentence and is false in every state where it is hidden. It
              shipped with no guard at all: while the panel was blocked on "not
              compared" it still promised "416 will be sent… they will still
              go", and after a run it sat beside "The run finished" while the
              terminal effect invalidated the counts query and `notSent` grew
              toward the whole list.
              That is Agent C's I4 defect — compare-population numbers narrated
              in the future tense about this run — on a line added by the commit
              that fixed I4 (/pr-review iter 2, Agent A N1). */}
          {(xp.exportTotal === 0 || ctaOffersARun) &&
            !held &&
            !compareQuery.isPending &&
            notAddedNote && (
              <p data-testid="export-scope-excluded" className="text-body-sm text-warning-ink">
                {/* The glyph sits OUTSIDE the testid: the sentence is pinned as a
                  literal, and decoration inside the asserted node would make a
                  tone change read as a copy change. */}
                <span aria-hidden="true">⚠ </span>
                {notAddedNote}
              </p>
            )}
          {/* A tally we could not read. The CTA is disabled beside it — and note
              what this does NOT do: fall back to counting rows client-side,
              which is the derivation the preview exists to replace and would
              fail silently with plausible numbers. */}
          {previewUnavailable && (
            <p data-testid="export-preview-unavailable" className="text-body-sm text-warning-ink">
              <span aria-hidden="true">⚠ </span>
              {EXPORT_PREVIEW_UNAVAILABLE}
            </p>
          )}
          {tooManySelected && (
            <p
              id={tooManyNoteId}
              data-testid="export-too-many"
              className="text-body-sm text-warning-ink"
            >
              {exportTooManySelected(xp.selectedIds.size, MAX_EXPLICIT_ADD_SELECTION)}
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <Button
            data-testid="export-cta"
            // A W1/W2/W3 conflict NEVER disables this (FR-13) — an unresolved
            // conflict is skipped, not a reason to stop the other 27 people.
            // What does disable it: an empty selection, an in-flight run, and —
            // since Trello #758 — a scope with no usable compare behind it.
            // That last term is the deliberate narrowing of the v37 rule; see
            // `compareBlockReason`.
            //
            // `dispatchCount`, not `selectedCount`: the settle effect runs
            // after paint, so for one frame a seed of nothing-but-already-
            // exported rows would pair a live button with a line saying there
            // is nothing to add.
            //
            // And not `pendingCount` either, which is what it read until
            // iter 3. THE BUTTON AND THE SENTENCE DELIBERATELY READ DIFFERENT
            // NUMBERS: the sentence answers "what will this write", the button
            // answers "is there a run here at all", and a ticked conflict with
            // no decision is a row that travels and is not written. Reading the
            // write count here disabled exactly the run the dialog one screen
            // earlier had just been corrected to allow — the same one-value-
            // two-questions defect as `selectAllScope.total`, on the other lane.
            // `confirmationLine` takes both so the pair stays honest.
            //
            // The terms this button actually refuses on live on `ctaOffersARun`,
            // not here: the two lists drifted while both were spelled out.
            aria-describedby={tooManySelected ? tooManyNoteId : undefined}
            disabled={!ctaOffersARun}
            onClick={() => xp.exportSelected(dispatchCount)}
          >
            {xp.isExporting && <Spinner size="sm" label="Exporting" />}
            {/* Mid-run copy stays in the ACTION's vocabulary. The heading above
                keeps naming the action ("Add Selected to search") because a
                title names a panel, not a state — so a generic "Working…" on
                the button was the only word on the surface that belonged to
                neither the old "Export" verb nor the new "Add" one, and read as
                a different feature's control (/pr-review iter 1, Agent C M3).
                Defaulted rather than hardcoded so a propless render still
                reads sensibly; there is no second surface relying on the
                default (Agent C M5). */}
            {xp.isExporting ? runningLabel : actionLabel}
          </Button>
        </div>
      </header>

      {/* Live progress bar (shown while a run is in flight). The Select-all
          checkbox that used to sit above it went with the rest of the selection
          model — see the file docblock. */}
      <div className="flex flex-col gap-2">
        {xp.isExporting && xp.exportTotal > 0 && (
          <div data-testid="export-progress" className="flex flex-col gap-1">
            <ProgressBar value={xp.exportProcessed} max={xp.exportTotal} label="Export progress" />
            <span className="text-caption text-text-secondary">
              Exporting… {xp.exportProcessed} of {xp.exportTotal}
            </span>
          </div>
        )}
        {/* A run with no denominator: `max={0}` would be an empty track that
            never fills, so the bar goes indeterminate instead of inventing a
            percentage. Not reachable through today's hook — both lanes set
            `exportTotal` in the same handler that starts the mutation — but it
            keeps "running, amount unknown" from rendering as nothing. */}
        {xp.isExporting && xp.exportTotal === 0 && (
          <div data-testid="export-progress" className="flex flex-col gap-1">
            <ProgressBar indeterminate live label="Export progress" />
            <span className="text-caption text-text-secondary">Exporting…</span>
          </div>
        )}
      </div>

      {/* ── The body, as one precedence chain ──────────────────────────────
          compareLoading → blocked → runSummary → unknown-outcome → the stats.

          There is NO `isExporting` branch, and an earlier version of this
          comment claimed one (/pr-review iter 1, Agent C M6). Mid-run the chain
          falls through to the stats, which is correct: the progress bar sits
          above it and the numbers stay on screen while they are being acted on.
          `isExporting` appears only as a GUARD on the runSummary branch — a
          terminal run flips it false in the same commit that fills
          `runSummary`, and without the guard the summary would paint over a run
          still in flight.

          The blocked state outranks everything below it, because a panel that
          cannot say what a write will do must not show numbers beside a button
          it has disabled. */}
      {compareQuery.isPending ? (
        <div
          data-testid="export-compare-loading"
          className="flex items-center gap-2 text-body text-text-secondary"
        >
          <Spinner size="sm" />
          Checking your last Compare to Thrive…
        </div>
      ) : blocked ? (
        <section
          data-testid="export-not-compared"
          data-reason={compareBlockReason}
          className="rounded-md border-l-4 border-warning-strong bg-warning-muted p-4"
        >
          <h3 className="text-h3 text-warning-ink">
            <span aria-hidden="true">⚠ </span>
            {compareBlockReason === "unreadable"
              ? EXPORT_COMPARE_UNREADABLE_TITLE
              : EXPORT_NOT_COMPARED_TITLE}
          </h3>
          <p className="mt-1 text-body text-text-secondary">
            {compareBlockReason === "failed"
              ? EXPORT_COMPARE_FAILED_BODY
              : compareBlockReason === "running"
                ? EXPORT_COMPARE_RUNNING_BODY
                : compareBlockReason === "unreadable"
                  ? EXPORT_COMPARE_UNREADABLE_BODY
                  : EXPORT_NOT_COMPARED_BODY}
          </p>
          {/* `unreadable` gets a RE-READ, not a re-run: the compare very likely
              exists and is fine — we failed to fetch it. Without this the state
              named a transient fault and told the recruiter to retry on a
              screen with nothing to retry with (Agent C N2). */}
          {/* Offered for any failing read EXCEPT the 404. `unreadable` needs no
              clause of its own — it is DEFINED as a failing read with no data,
              so `isError` already covers it (/pr-review iter 6, Agent A §7).
              Gated on the reason alone, a `running` compare whose polls started failing kept its
              "wait for it to finish" copy with nothing polling and no way back
              (/pr-review iter 4, Agent A §3a) — so the button follows the
              FAILURE. But `never` is itself an `isError` state (it is defined as
              a 404 with no data), and re-reading a 404 is the one thing this
              file says twice is pointless: nothing has ever been compared, and
              no number of reads changes that. Offering `Try again` above
              `Compare to Thrive` in the panel's commonest blocked state
              contradicts the rule and the decision behind it (/pr-review iter 5,
              Agent C). */}
          {compareQuery.isError && compareBlockReason !== "never" && (
            <Button
              variant="secondary"
              type="button"
              data-testid="export-retry-compare-read"
              className="mt-3"
              // A read takes a round trip, and without this the button gave no
              // sign it had been pressed — so the natural response to a slow
              // retry is to press it again (/pr-review iter 3, Agent C M1).
              //
              // `aria-busy` because `disabled` alone says the control is
              // unavailable, not that a read is in flight — a different fact,
              // and the one an earlier comment here optimistically conflated
              // with it (/pr-review iter 6, Agent A §2b). The spinner cannot
              // carry it either: it is `aria-hidden` precisely so it stops
              // polluting this button's accessible name.
              //
              // What `aria-busy` buys is the correct STATE, not an
              // announcement. On a disabled, unfocused button outside any live
              // region, no AT speaks this unprompted; a reader who navigates
              // to the button gets it. Claiming more would repeat the mistake
              // above in the other direction (/pr-review iter 7, Agent C M2).
              aria-busy={compareQuery.isFetching}
              disabled={compareQuery.isFetching}
              onClick={() => void compareQuery.refetch()}
            >
              {/* `aria-hidden`, because omitting `label` does not help: it
                  DEFAULTS to "Loading" (`packages/ui/src/primitives/Spinner.tsx`),
                  so the button's accessible name went from "Retrying Try again"
                  to "Loading Try again" — the iter-4 fix asserted a mechanism it
                  did not achieve (/pr-review iter 5, Agent C). Hiding the
                  decoration leaves the name as the button's own text; the
                  pending read is carried by `aria-busy` above, NOT by
                  `disabled` — this clause used to say `disabled`, which is the
                  very conflation the comment above corrects (/pr-review iter 7,
                  Agent C M1). */}
              {compareQuery.isFetching && (
                <span aria-hidden="true">
                  <Spinner size="sm" />
                </span>
              )}
              {EXPORT_RETRY_READ_CTA}
            </Button>
          )}
          {/* Offered only where running one would HELP.
              · `running` — the way out is to wait. A button offering to start
                what is already running invites a second run, and for the first
                hour the server refuses the reclaim that would justify one; see
                the note beside `compareBlockReason`.
              · `unreadable` — the compare may well exist and be perfectly good;
                we could not read it. Sending the recruiter to burn a full dry
                run on our outage is the opposite of a fix. */}
          {onRequestCompare &&
            compareBlockReason !== "running" &&
            compareBlockReason !== "unreadable" && (
              <Button
                variant="secondary"
                type="button"
                data-testid="export-run-compare"
                className="mt-3"
                onClick={onRequestCompare}
              >
                {EXPORT_RUN_COMPARE_CTA}
              </Button>
            )}
        </section>
      ) : xp.runSummary && !xp.isExporting ? (
        /* ONE aggregated result, not a badge per row. `data-status` carries the
           partial/succeeded verdict for tests and for anything styling on it;
           the HEADING carries it for the person reading, because that is the
           part they are guaranteed to see. */
        <section
          data-testid="export-result-summary"
          data-status={xp.runSummary.status}
          className={`rounded-md border-l-4 p-4 ${
            xp.runSummary.status === "partial"
              ? "border-error bg-surface"
              : "border-success bg-surface"
          }`}
        >
          <h3 className="text-h3 text-text">
            {exportResultHeading(xp.runSummary.exported, xp.runSummary.total, xp.runSummary.failed)}
          </h3>
          <ul className="mt-1 space-y-0.5">
            {xp.runSummary.skipped > 0 && (
              <li data-testid="export-result-skipped" className="text-body text-text-secondary">
                {exportNothingWrittenLine(xp.runSummary.skipped)}
              </li>
            )}
            {xp.runHeldBackCount > 0 && (
              <li
                data-testid="export-result-needs-compare"
                className="text-body text-text-secondary"
              >
                {exportResultNeedsCompareLine(xp.runHeldBackCount)}
              </li>
            )}
            {xp.runSummary.pending > 0 && (
              <li data-testid="export-result-pending" className="text-body text-text-secondary">
                {exportResultPendingLine(xp.runSummary.pending)}
              </li>
            )}
          </ul>
          <p className="mt-2 text-caption text-text-muted">{EXPORT_RESULT_DETAIL_POINTER}</p>
        </section>
      ) : xp.exportTotal > 0 && !xp.isExporting && !xp.runSummary ? (
        /* Terminal, ours, and the payload carried none of our rows — the
           outcome is UNKNOWN. It gets its own sentence rather than a zeroed
           summary: "Added 0 of 0" asserts a result we do not have. Mirrors the
           hook's `"info"` toast for the same state. */
        <div>
          <p data-testid="export-result-unknown" className="text-body text-text-secondary">
            {EXPORT_RESULT_UNKNOWN}
          </p>
          {/* What the run left out is known from the 202 even when its outcome
              is not (/pr-review iter 7, Agent C m3). */}
          {xp.runHeldBackCount > 0 && (
            <p data-testid="export-result-needs-compare" className="text-body text-text-secondary">
              {exportResultNeedsCompareLine(xp.runHeldBackCount)}
            </p>
          )}
        </div>
      ) : (
        /* THE LAST COMPARE'S RECAP LIVED HERE, and is gone (product owner,
           2026-09-17). It was `CompareBucketList` + `exportCompareAgeLine` +
           `exportConflictsLine`, every number read from the stored
           `dedup_status`. `build_dedup_status` records NO SCOPE, so those
           numbers described whatever the last run happened to cover, printed
           beside a selection that had nothing to do with it: two contacts
           ticked, "118" on screen. That is the report this card closes.

           Everything the panel now states about the run comes from
           `previewQuery`, scoped to the actual selection. `CompareBucketList`
           itself survives — the Compare modal renders it under the `compare-*`
           prefix, where a recap of the run IS the subject.

           `export-ready` replaces `export-stats` as the "compare read landed,
           panel unblocked" signal. Twenty tests across two files waited on that
           testid without ever asserting its content, and they need somewhere to
           wait. Deliberately NOT the confirmation line: that carries an extra
           lifecycle term of its own, so it is absent for part of a run and any
           test waiting after dispatch would hang.

           CONTENT-FREE ON PURPOSE, and that has a consequence for tests: an
           empty div has no box, so Playwright's `toBeVisible()` is false for it
           forever. Wait on ATTACHMENT — `toBeAttached()`, or `.count() > 0` as
           `add-panel.ts` does. One E2E assertion used visibility and sat out a
           60s timeout for it (2026-09-18, `export.spec.ts:651`). React Testing
           Library's `findByTestId` is presence-based, which is why every vitest
           wait on this passed throughout and the gap only showed in Playwright. */
        <div data-testid="export-ready" />
      )}
    </div>
  );
}
